/**
 * Icon Banner Component
 * Handles the icon banner section with dynamic marquee functionality
 */

class IconBanner extends HTMLElement {
  constructor() {
    super();
    this.paddingWrapper = null;
    this.container = null;
    this.isMarqueeActive = false;
    this.resizeObserver = null;
    this.animationFrameId = null;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.paddingWrapper = this.querySelector('.container-padding');
    this.container = this.querySelector('.icon-banner__container');
    
    if (!this.container || !this.paddingWrapper) {
      return;
    }

    this.setupAccessibility();
    this.setupMediaQuery();
    this.setupResizeObserver();
    this.checkAndInitMarquee();
  }

  setupAccessibility() {
    // Ensure all icons have proper alt text
    const icons = this.querySelectorAll('.icon-banner__icon img');
    icons.forEach(icon => {
      if (!icon.alt || icon.alt.trim() === '') {
        const textElement = icon.closest('.icon-banner__item')?.querySelector('.icon-banner__text');
        if (textElement) {
          icon.alt = textElement.textContent.trim();
        }
      }
    });
  }

  setupMediaQuery() {
    const mediaQuery = window.matchMedia('(min-width: 1440px)');
    
    const handleMediaChange = (e) => {
      if (e.matches) {
        this.disableMarquee();
      } else {
        this.checkAndInitMarquee();
      }
    };

    mediaQuery.addEventListener('change', handleMediaChange);
  }

  setupResizeObserver() {
    this.resizeObserver = new ResizeObserver(() => {
      if (window.innerWidth < 1440) {
        this.checkAndInitMarquee();
      }
    });

    this.resizeObserver.observe(this.paddingWrapper);
  }

  checkAndInitMarquee() {
    if (window.innerWidth >= 1440) {
      this.disableMarquee();
      return;
    }
    
    // Get the actual container (could be inside marquee wrapper or not)
    const actualContainer = this.querySelector('.icon-banner__container:not(.icon-banner__container--clone)') || this.container;
    
    // Get computed style to check for gaps
    const containerStyle = window.getComputedStyle(actualContainer);
    const gapValue = parseFloat(containerStyle.gap) || 0;
    
    // Calculate actual content width by measuring all items
    const items = actualContainer.querySelectorAll('.icon-banner__item');
    let totalContentWidth = 0;
    let itemWidths = [];
    
    items.forEach((item, index) => {
      const width = item.offsetWidth;
      itemWidths.push(width);
      totalContentWidth += width;
      // Add gap between items
      if (index < items.length - 1 && gapValue > 0) {
        totalContentWidth += gapValue;
      }
    });
    
    // Get available width from container-padding element
    const paddingStyle = window.getComputedStyle(this.paddingWrapper);
    const paddingLeft = parseFloat(paddingStyle.paddingLeft) || 0;
    const paddingRight = parseFloat(paddingStyle.paddingRight) || 0;
    const availableWidth = this.paddingWrapper.clientWidth - paddingLeft - paddingRight;
    
    if (totalContentWidth > availableWidth && !this.isMarqueeActive) {
      this.enableMarquee();
    } else if (totalContentWidth <= availableWidth && this.isMarqueeActive) {
      this.disableMarquee();
    }
  }

  enableMarquee() {
    if (this.isMarqueeActive) return;
    
    this.isMarqueeActive = true;
    
    // Create wrapper for infinite scroll
    const wrapper = document.createElement('div');
    wrapper.classList.add('icon-banner__marquee-wrapper');
    
    // Clone container for seamless loop
    const clone = this.container.cloneNode(true);
    clone.classList.add('icon-banner__container--clone');
    
    // Move container into wrapper
    this.paddingWrapper.appendChild(wrapper);
    wrapper.appendChild(this.container);
    wrapper.appendChild(clone);
    
    // Start animation
    this.startMarqueeAnimation(wrapper);
  }

  startMarqueeAnimation(wrapper) {
    // Cancel any existing animation
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    const containerWidth = this.container.scrollWidth;
    const speed = 30;
    let position = 0;
    let lastTime = performance.now();

    const animate = (currentTime) => {
      if (!this.isMarqueeActive) {
        this.animationFrameId = null;
        return;
      }
      
      const deltaTime = currentTime - lastTime;
      lastTime = currentTime;
      
      position -= (speed * deltaTime) / 1000;
      
      if (Math.abs(position) >= containerWidth) {
        position = 0;
      }

      wrapper.style.transform = `translateX(${position}px)`;
      this.animationFrameId = requestAnimationFrame(animate);
    };

    this.animationFrameId = requestAnimationFrame(animate);
  }

  disableMarquee() {
    if (!this.isMarqueeActive) return;
    
    this.isMarqueeActive = false;
    
    // Cancel animation frame
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    
    const wrapper = this.paddingWrapper.querySelector('.icon-banner__marquee-wrapper');
    if (wrapper) {
      const originalContainer = wrapper.querySelector('.icon-banner__container:not(.icon-banner__container--clone)');
      if (originalContainer) {
        this.paddingWrapper.appendChild(originalContainer);
      }
      wrapper.remove();
    }
  }

  disconnectedCallback() {
    this.disableMarquee();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }
}

// Register the custom element
if (!customElements.get('icon-banner')) {
  customElements.define('icon-banner', IconBanner);
}

// Initialize icon banners
function initIconBanners() {
  const banners = document.querySelectorAll('section.icon-banner');
  
  banners.forEach((banner) => {
    const paddingWrapper = banner.querySelector('.container-padding');
    if (paddingWrapper) {
      const container = paddingWrapper.querySelector('.icon-banner__container');
      if (container) {
        // Upgrade to custom element
        const iconBanner = document.createElement('icon-banner');
        Array.from(banner.attributes).forEach(attr => {
          iconBanner.setAttribute(attr.name, attr.value);
        });
        iconBanner.innerHTML = banner.innerHTML;
        banner.replaceWith(iconBanner);
      }
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initIconBanners);
} else {
  initIconBanners();
}
